/**
 * The seams between the interfaces and the platform. Every surface talks to
 * one of these gateways; each has a live implementation (the backend, through
 * the gateway that adds the credential) and a demo implementation (typed
 * fixtures). A screen never knows which one it has, except through `mode`,
 * which the shell uses to mark demo data as demo data.
 *
 * Where the platform has no backend for something yet (manufacturing test
 * stations, incident history, API request logs) the live implementation
 * answers `{ available: false }` and the screen explains what will appear;
 * it never falls back to fixtures.
 */
import type {
  ApiRequestLogEntry,
  AttentionCase,
  Batch,
  DeviceDetail,
  DeviceSummary,
  EnergyHistory,
  HistoryRange,
  MachineClient,
  ManufacturedUnit,
  OrderSummary,
  Organisation,
  PlatformHealth,
  ServiceStatus,
  StockItem,
  WebhookDelivery,
  WebhookHealth,
} from '../domain/model';

export type DataMode = 'live' | 'demo';

export type Availability<T> = { available: true; value: T } | { available: false; reason: string };

export type DeviceCommandType = 'request_state' | 'restart' | 'backfill';
export type ShipmentStep = 'packed' | 'shipped' | 'delivered';

/** A failed platform call, carrying what an error screen needs. */
export class GatewayError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly request: string,
  ) {
    super(message);
    this.name = 'GatewayError';
  }
  /** Worth trying again without changing anything. */
  get retryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }
}

/** app.p1plug.nl: exactly one device, the one the gateway is bound to. */
export interface LabGateway {
  mode: DataMode;
  device(): Promise<DeviceDetail>;
  history(range: HistoryRange): Promise<EnergyHistory>;
}

/** backoffice, factory and fulfilment: the P1Plug-internal view. */
export interface OpsGateway {
  mode: DataMode;
  health(): Promise<PlatformHealth>;
  devices(): Promise<DeviceSummary[]>;
  device(id: string): Promise<DeviceDetail>;
  history(id: string, range: HistoryRange): Promise<EnergyHistory>;
  /** Null when commands can be sent; otherwise why not. */
  commandsUnavailable: string | null;
  sendCommand(id: string, type: DeviceCommandType): Promise<void>;
  organisations(): Promise<Organisation[]>;
  deliveries(organisationId: string): Promise<WebhookDelivery[]>;
  orders(): Promise<OrderSummary[]>;
  order(id: string): Promise<OrderSummary>;
  stock(): Promise<StockItem[]>;
  attention(): Promise<AttentionCase[]>;
  assignDevice(orderId: string, serial: string): Promise<OrderSummary>;
  advanceShipment(orderId: string, step: ShipmentStep): Promise<OrderSummary>;
}

export interface TenantDevice extends DeviceSummary {
  customer: { name: string; address: string; city: string | null } | null;
  lastDataAt: string | null;
  netW: number | null;
  installationNo: number | null;
  meterPointId: string;
  orderIds: string[];
}

/** portal and developers: one customer organisation, through the public API. */
export interface TenantGateway {
  mode: DataMode;
  organisation(): Promise<{ id: string; name: string; slug: string; credential: string; scopes: string[] }>;
  devices(): Promise<TenantDevice[]>;
  device(id: string): Promise<TenantDevice>;
  orders(): Promise<OrderSummary[]>;
  order(id: string): Promise<OrderSummary>;
  webhook(): Promise<WebhookHealth>;
  deliveries(): Promise<WebhookDelivery[]>;
  clients(): Promise<Availability<MachineClient[]>>;
  requestLog(): Promise<Availability<ApiRequestLogEntry[]>>;
}

export interface ManufacturingGateway {
  mode: DataMode;
  /** Whether a manufacturing backend (test stations, batches) is connected. */
  connected: Availability<true>;
  batches(): Promise<Batch[]>;
  units(batchId: string): Promise<ManufacturedUnit[]>;
  /** The unit behind a scanned serial, or null when the serial is unknown. */
  unit(serial: string): Promise<ManufacturedUnit | null>;
  /** The result the station records for a unit that has not been tested yet. */
  test(serial: string): Promise<ManufacturedUnit>;
}

export interface StatusGateway {
  mode: DataMode;
  status(): Promise<ServiceStatus>;
}

export interface Gateways {
  mode: DataMode;
  lab: LabGateway;
  ops: OpsGateway;
  tenant: TenantGateway;
  manufacturing: ManufacturingGateway;
  status: StatusGateway;
}
